import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, CalendarFilter, FilterDropdown, FilterDropdownBasicItem, FilterDropdownTextItem, MonthPicker, YearPicker, } from '../../components';
import { classNames, getDateLast15MinuteStep, withTheme } from '../../utils';
import { convertToSiteTz, endOfDayTz, getEndOfMonth, startOfDayTz, getStartOfMonth, isToday, getStartOfDay, } from '../../utils/date';
import DateFnsUtils from '@date-io/date-fns';
import { DatePicker, KeyboardTimePicker, MuiPickersUtilsProvider, } from '@material-ui/pickers';
import { differenceInDays, isFuture, isSameMinute, subDays } from 'date-fns';
import debounce from 'lodash.debounce';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { IoMdArrowDropleft, IoMdArrowDropright } from 'react-icons/io';
import { useViewportType, ViewportTypes } from '../../hooks/useViewportType';
import { createNotificationDateFilter, formatDateFilterTitle, } from '../../utils/dateFilter';
import styles from './DateFilter.module.css';
import IconClock from '../../icons/ui/IconClock';
const DateFilter = ({ className, value, setFilter, alignItemsRight = false, limitRangeSelection = 31, disabled = false, disableCustomRange = false, optionCreator = createNotificationDateFilter, customPickerVariant = 'datetime', dateFormat = 'date', useCurrentTime, allowFutureDates = false, hideArrowButtons = false, ianaTimeZoneCode, locale, displayDatesAsTitle = true, fullSize = false, selectedMonthIndex = 0, setSelectedMonthIndex = () => { }, selectedYear = 0, setSelectedYear = () => { }, disableBefore, disableAfter, is12HrTimeFormat, dropdownClassname, }) => {
    const isMobile = useViewportType() === ViewportTypes.MOBILE;
    const currentTimeRoundedToLast15MinuteStep = getDateLast15MinuteStep();
    const processedToDate = useCurrentTime
        ? DateTime.fromJSDate(new Date()).setZone(ianaTimeZoneCode).toJSDate()
        : DateTime.fromJSDate(currentTimeRoundedToLast15MinuteStep)
            .setZone(ianaTimeZoneCode)
            .toJSDate();
    const startOfYesterday = DateTime.fromJSDate(processedToDate)
        .minus({ day: 1 })
        .toJSDate();
    const isMonthlyPicker = customPickerVariant === 'monthly';
    const isYearPicker = customPickerVariant === 'yearly';
    // ************** useState **************** //
    const [options, setOptions] = useState(optionCreator(ianaTimeZoneCode));
    const [customPickerOpen, setCustomPickerOpen] = useState(false);
    const [customDropdownOpen, setCustomDropdownOpen] = useState(false);
    const [dateTimePickerFromOpen, setDateTimePickerFromOpen] = useState(false);
    const [dateTimePickerToOpen, setDateTimePickerToOpen] = useState(false);
    const [customRange, setCustomRange] = useState({
        from: (value === null || value === void 0 ? void 0 : value.fromDate) || startOfYesterday,
        to: (value === null || value === void 0 ? void 0 : value.toDate) || processedToDate,
    });
    const [tempRange, setTempRange] = useState({
        from: startOfYesterday,
        to: processedToDate,
    });
    const [error, setError] = useState('');
    // ************** useEffect **************** //
    useEffect(() => {
        setOptions(optionCreator(ianaTimeZoneCode));
    }, [optionCreator, setOptions, ianaTimeZoneCode]);
    useEffectResetFilterIfPreviousSelectionNoLongerAnOption(setFilter, value, options, disableCustomRange);
    useEffectUpdateDateFilterIfPlatformKeptOpen(setOptions, () => optionCreator(ianaTimeZoneCode), setFilter, value);
    useEffect(() => {
        if (isMonthlyPicker) {
            setFilter({
                fromDate: new Date(selectedYear, selectedMonthIndex, 1),
                toDate: subDays(new Date(selectedYear, selectedMonthIndex + 1, 1), 1),
                isMonthly: true,
            });
        }
    }, [selectedMonthIndex, selectedYear]);
    useEffect(() => {
        if (!customPickerOpen || !customDropdownOpen) {
            setTempRange({
                from: null,
                to: null,
            });
        }
    }, [customPickerOpen, customDropdownOpen, customRange]);
    useEffect(() => {
        if (!tempRange.from) {
            const initialFrom = new Date();
            initialFrom.setHours(0, 0, 0, 0);
            setTempRange((prev) => (Object.assign(Object.assign({}, prev), { from: initialFrom })));
            setCustomRange((prev) => (Object.assign(Object.assign({}, prev), { from: initialFrom })));
        }
    }, []);
    // ************ local var/functions ************** //
    const showTimeOfDay = dateFormat === 'datetime';
    const { from: customFrom, to: customTo } = customRange;
    const customRangeErrorMessage = (() => {
        if (!customFrom || !customTo)
            return null;
        const isStartBeforeEnd = customFrom < customTo;
        if (!isStartBeforeEnd) {
            return 'The start of the date range must precede the end.';
        }
        const isValidRangeLength = Math.abs(differenceInDays(customFrom, customTo)) <= limitRangeSelection;
        if (!isValidRangeLength) {
            return `The range should not exceed ${limitRangeSelection} days.`;
        }
        if (error.length)
            return error;
        return null;
    })();
    const determineArrowValue = (value, arrowDirection) => {
        if (!value || !value.fromDate || !value.toDate)
            return null;
        const { fromDate, toDate, isMonthly } = value;
        const customShowTimeOfDay = customPickerVariant === 'datetime';
        const newDates = (() => {
            let newFromDate;
            let newToDate;
            if (isMonthly) {
                const previousMonth = DateTime.fromJSDate(fromDate)
                    .setZone(ianaTimeZoneCode)
                    .minus({ month: 1 })
                    .toJSDate();
                const nextMonth = DateTime.fromJSDate(toDate)
                    .setZone(ianaTimeZoneCode)
                    .plus({ month: 1 })
                    .toJSDate();
                const endOfNextMonth = getEndOfMonth(nextMonth, ianaTimeZoneCode);
                const endOfYesterday = DateTime.fromJSDate(new Date())
                    .setZone(ianaTimeZoneCode)
                    .minus({ day: 1 })
                    .endOf('day')
                    .toJSDate();
                const leftArrowFromDate = getStartOfMonth(previousMonth, ianaTimeZoneCode);
                const leftArrowToDate = getEndOfMonth(previousMonth, ianaTimeZoneCode);
                const rightArrowFromDate = getStartOfMonth(nextMonth, ianaTimeZoneCode);
                const rightArrowToDate = !allowFutureDates && isFuture(endOfNextMonth)
                    ? endOfYesterday
                    : endOfNextMonth;
                newFromDate =
                    arrowDirection === 'left' ? leftArrowFromDate : rightArrowFromDate;
                newToDate =
                    arrowDirection === 'left' ? leftArrowToDate : rightArrowToDate;
            }
            else {
                const previousFromDateMinusOneSecond = DateTime.fromJSDate(startOfDayTz(fromDate, ianaTimeZoneCode))
                    .minus({ second: 1 })
                    .toJSDate();
                const previousToDatePlusOneSecond = DateTime.fromJSDate(endOfDayTz(toDate, ianaTimeZoneCode))
                    .plus({ second: 1 })
                    .toJSDate();
                const intervalInDays = differenceInDays(toDate, fromDate);
                const leftArrowFromDate = startOfDayTz(DateTime.fromJSDate(previousFromDateMinusOneSecond)
                    .minus({ days: intervalInDays })
                    .toJSDate(), ianaTimeZoneCode);
                const leftArrowToDate = previousFromDateMinusOneSecond;
                const rightArrowFromDate = previousToDatePlusOneSecond;
                let rightArrowToDate = endOfDayTz(DateTime.fromJSDate(previousToDatePlusOneSecond)
                    .setZone(ianaTimeZoneCode)
                    .plus({ days: intervalInDays })
                    .toJSDate(), ianaTimeZoneCode);
                //this logic allows one day intervals
                //to move forward up to today (current time)
                if (intervalInDays < 1 &&
                    !allowFutureDates &&
                    isFuture(rightArrowToDate)) {
                    rightArrowToDate = processedToDate;
                }
                newFromDate =
                    arrowDirection === 'left' ? leftArrowFromDate : rightArrowFromDate;
                newToDate =
                    arrowDirection === 'left' ? leftArrowToDate : rightArrowToDate;
            }
            return {
                fromDate: newFromDate,
                toDate: newToDate,
            };
        })();
        return {
            title: formatDateFilterTitle(newDates.fromDate, newDates.toDate, locale.dateFormat, customShowTimeOfDay ? locale.timeFormat : undefined, ianaTimeZoneCode, true),
            fromDate: newDates.fromDate,
            toDate: newDates.toDate,
            isCustomRange: true,
            isMonthly,
        };
    };
    const isDisableBefore = (leftArrowValue) => {
        const disableBeforeAry = (disableBefore === null || disableBefore === void 0 ? void 0 : disableBefore.split('-')) || [];
        const disableBeforeDate = new Date(Number(disableBeforeAry[0]) || 0, Number(disableBeforeAry[1]) - 1 || 0, Number(disableBeforeAry[2]) || 1);
        if (isMonthlyPicker) {
            return (disableBeforeDate.getFullYear() >
                leftArrowValue.fromDate.getFullYear() &&
                disableBeforeDate.getMonth() > leftArrowValue.fromDate.getMonth());
        }
        if (isYearPicker) {
            return (disableBeforeDate.getFullYear() > leftArrowValue.fromDate.getFullYear());
        }
        return disableBeforeDate > leftArrowValue.fromDate;
    };
    const isDisableAfter = (arrowValue) => {
        const disableAfterAry = (disableAfter === null || disableAfter === void 0 ? void 0 : disableAfter.split('-')) || [];
        if (disableAfterAry.length === 0) {
            return false;
        }
        const disableAfterDate = new Date(Number(disableAfterAry[0]) || 0, Number(disableAfterAry[1]) - 1 || 0, Number(disableAfterAry[2]) || 1);
        if (isMonthlyPicker) {
            return (disableAfterDate.getFullYear() < arrowValue.fromDate.getFullYear() &&
                disableAfterDate.getMonth() < arrowValue.fromDate.getMonth());
        }
        if (isYearPicker) {
            return disableAfterDate.getFullYear() < arrowValue.fromDate.getFullYear();
        }
        return disableAfterDate < arrowValue.fromDate;
    };
    const debouncedSetFilter = debounce(setFilter, 100, { maxWait: 1000 });
    const leftArrowValue = determineArrowValue(value, 'left');
    const rightArrowValue = determineArrowValue(value, 'right');
    const isLeftArrowDisabled = disabled || !leftArrowValue || isDisableBefore(leftArrowValue);
    const intervalInDays = rightArrowValue &&
        rightArrowValue.fromDate &&
        rightArrowValue.toDate &&
        differenceInDays(rightArrowValue.toDate, rightArrowValue.fromDate);
    const isRightArrowDisabled = disabled ||
        !rightArrowValue ||
        isDisableAfter(rightArrowValue) ||
        (!allowFutureDates && isFuture(rightArrowValue.fromDate)) ||
        // should ignore toDate validation when allow future dates
        (!allowFutureDates &&
            isFuture(rightArrowValue.toDate) &&
            intervalInDays > 1) ||
        rightArrowValue.fromDate.valueOf() > rightArrowValue.toDate.valueOf();
    const onArrowButtonClick = (arrowDirection) => {
        if (!!leftArrowValue && !!rightArrowValue) {
            debouncedSetFilter(arrowDirection === 'left' ? leftArrowValue : rightArrowValue);
        }
    };
    const applyCustomDate = () => {
        let { from, to } = tempRange.from ? tempRange : customRange;
        if (!from) {
            return null;
        }
        if (!to) {
            to = new Date(from);
            if (customRange.to) {
                to.setHours(customRange.to.getHours());
                to.setMinutes(customRange.to.getMinutes());
            }
        }
        const isDateTimePicker = customPickerVariant === 'datetime' || customPickerVariant === 'date';
        const isMobileDatePicker = customPickerVariant === 'date' && isMobile;
        const shouldKeepLocalTime = isDateTimePicker || isMobileDatePicker;
        const fromDate = convertToSiteTz(from, ianaTimeZoneCode, {
            keepLocalTime: shouldKeepLocalTime,
        });
        const toDate = convertToSiteTz(to, ianaTimeZoneCode, {
            keepLocalTime: shouldKeepLocalTime,
        });
        if (customPickerVariant === 'datetime') {
            setFilter({
                title: formatDateFilterTitle(from, to, locale.dateFormat, locale.timeFormat, ianaTimeZoneCode, true),
                fromDate,
                toDate,
                isCustomRange: true,
            });
        }
        else {
            setFilter({
                title: formatDateFilterTitle(from, to, locale.dateFormat, showTimeOfDay ? locale.timeFormat : undefined, ianaTimeZoneCode, true),
                fromDate,
                toDate,
                isCustomRange: true,
            });
        }
        setCustomRange({
            from: from,
            to: to,
        });
        setCustomDropdownOpen(false);
        setCustomPickerOpen(false);
    };
    const renderFilterDropdownTitle = () => {
        if (value && value.title && !displayDatesAsTitle)
            return getDateContainer(value.title);
        if (value && value.fromDate && value.toDate) {
            if (value.isCustomRange) {
                return getDateContainer(formatDateFilterTitle(value.fromDate, value.toDate, locale.dateFormat, customPickerVariant === 'datetime' ? locale.timeFormat : undefined, ianaTimeZoneCode, true));
            }
            return getDateContainer(formatDateFilterTitle(value.fromDate, value.toDate, locale.dateFormat, showTimeOfDay ? locale.timeFormat : undefined, ianaTimeZoneCode, true));
        }
        return 'Choose date range';
    };
    const handleFromDateChange = (newDate) => {
        if (newDate && newDate.valueOf()) {
            const updatedDate = new Date(tempRange.from || new Date());
            updatedDate.setHours(newDate.getHours());
            updatedDate.setMinutes(newDate.getMinutes());
            setTempRange((prev) => (Object.assign(Object.assign({}, prev), { from: updatedDate })));
            setCustomRange((prev) => (Object.assign(Object.assign({}, prev), { from: updatedDate })));
            setError('');
        }
        else {
            setError('Enter a valid Start Time');
        }
    };
    const handleToDateChange = (newDate) => {
        if (newDate && newDate.valueOf()) {
            const updatedDate = new Date(tempRange.to || new Date());
            updatedDate.setHours(newDate.getHours());
            updatedDate.setMinutes(newDate.getMinutes());
            if (tempRange.to)
                setTempRange((prev) => (Object.assign(Object.assign({}, prev), { to: updatedDate })));
            setCustomRange((prev) => (Object.assign(Object.assign({}, prev), { to: updatedDate })));
            setError('');
        }
        else {
            setError('Enter a valid End Time');
        }
    };
    const customDateTimePicker = (_jsxs(_Fragment, { children: [_jsx(CalendarFilter, { disableFuture: true, disableBefore: disableBefore, limitRangeSelection: limitRangeSelection, setDateRange: (from, to) => {
                    //site timezone conversion happens inside CalendarFilter
                    //b/c sometimes we use CalendarFilter independently
                    // setCustomRange({
                    //   from,
                    //   to,
                    // });
                    setTempRange({
                        from,
                        to,
                    });
                    if (from) {
                        setCustomRange({
                            from: startOfDayTz(from),
                            to: endOfDayTz(from),
                        });
                    }
                }, presetDates: value, ianaTimeZoneCode: ianaTimeZoneCode }, void 0), _jsxs("div", Object.assign({ className: styles.timePicker }, { children: [_jsxs("div", { children: [_jsx("span", { children: "Start Time" }, void 0), _jsx(KeyboardTimePicker, { onChange: (newDate) => {
                                    if (newDate) {
                                        handleFromDateChange(newDate);
                                    }
                                }, value: customRange.from, onOpen: () => setDateTimePickerFromOpen(true), onClose: () => setDateTimePickerFromOpen(false), keyboardIcon: _jsx(IconClock, { size: 16, className: styles.clockIcon }, void 0), InputProps: { className: styles.KeyboardTimePicker }, placeholder: is12HrTimeFormat ? '12:00 AM' : '00:00', ampm: is12HrTimeFormat }, void 0)] }, void 0), _jsxs("div", { children: [_jsx("span", { children: "End Time" }, void 0), _jsx(KeyboardTimePicker, { value: customRange.to, onChange: (newDate) => {
                                    if (newDate) {
                                        handleToDateChange(newDate);
                                    }
                                }, InputProps: { className: styles.KeyboardTimePicker }, onOpen: () => setDateTimePickerFromOpen(true), onClose: () => setDateTimePickerFromOpen(false), placeholder: is12HrTimeFormat ? '11:59 PM' : '23:59', ampm: is12HrTimeFormat, keyboardIcon: _jsx(IconClock, { size: 16, className: styles.clockIcon }, void 0) }, void 0)] }, void 0)] }), void 0)] }, void 0));
    //we keep these dates in local browser timezone to show correct dates in the Picker itself
    //when we apply custom date change, then we change timezone there
    const customDatePicker = (_jsx(CalendarFilter, { disableFuture: true, disableBefore: disableBefore, limitRangeSelection: limitRangeSelection, setDateRange: (from, to) => {
            //site timezone conversion happens inside CalendarFilter
            //b/c sometimes we use CalendarFilter independently
            // setCustomRange({
            //   from,
            //   to,
            // });
            setTempRange({
                from,
                to,
            });
        }, presetDates: value, ianaTimeZoneCode: ianaTimeZoneCode }, void 0));
    //we keep these dates in local browser timezone to show correct dates in the Picker itself
    //when we apply custom date change, then we change timezone there
    const customMobileDatePicker = (_jsxs(_Fragment, { children: [_jsx(DatePicker, { label: "From", value: customRange.from, onChange: (newDate) => {
                    if (newDate) {
                        const isoDate = DateTime.fromJSDate(newDate).toISODate();
                        const startOfNewDate = getStartOfDay(isoDate);
                        setCustomRange(Object.assign(Object.assign({}, customRange), { from: DateTime.fromISO(startOfNewDate).toJSDate() }));
                    }
                }, format: locale.dateFormat, open: dateTimePickerFromOpen, onOpen: () => setDateTimePickerFromOpen(true), onClose: () => setDateTimePickerFromOpen(false), disableFuture: true }, void 0), _jsx(DatePicker, { label: "To", value: customRange.to, onChange: (newDate) => {
                    if (newDate) {
                        const newDateInIso = DateTime.fromJSDate(newDate).toISO();
                        //if selected date it today, then we want most recent time,
                        //not the end of the day
                        const endOfNewDate = isToday(newDateInIso, ianaTimeZoneCode)
                            ? processedToDate
                            : endOfDayTz(newDate);
                        setCustomRange(Object.assign(Object.assign({}, customRange), { to: endOfNewDate }));
                    }
                }, format: locale.dateFormat, open: dateTimePickerToOpen, onOpen: () => setDateTimePickerToOpen(true), onClose: () => setDateTimePickerToOpen(false), disableFuture: true }, void 0)] }, void 0));
    const customMonthlyPicker = (_jsx(_Fragment, { children: _jsx(MonthPicker, { disableFuture: true, disableBefore: disableBefore, disableAfter: disableAfter, selectedMonthIndex: selectedMonthIndex, setSelectedMonthIndex: setSelectedMonthIndex, selectedYear: selectedYear, setSelectedYear: setSelectedYear }, void 0) }, void 0));
    const customYearPicker = (_jsx(_Fragment, { children: _jsx(YearPicker, { selectedYear: selectedYear, setSelectedYear: setSelectedYear, disableFuture: true, disableBefore: disableBefore, disableAfter: disableAfter }, void 0) }, void 0));
    return (_jsxs("div", Object.assign({ className: classNames(styles.root, className, fullSize ? styles.fullSize : '') }, { children: [!hideArrowButtons && (_jsx("div", Object.assign({ className: isLeftArrowDisabled
                    ? styles.disabledLeftArrowButtonContainer
                    : styles.leftArrowButtonContainer, onClick: isLeftArrowDisabled ? undefined : () => onArrowButtonClick('left') }, { children: _jsx(IoMdArrowDropleft, { size: 20, className: isLeftArrowDisabled ? styles.disabledArrowIcon : styles.arrowIcon }, void 0) }), void 0)), !isMonthlyPicker && !isYearPicker && (_jsxs(FilterDropdown, Object.assign({ className: fullSize ? styles.fullSize : '', buttonClassName: classNames(styles.filterButton, fullSize ? styles.fullSize : ''), dropdownClassName: classNames(dropdownClassname, styles.noMaxHeight), title: renderFilterDropdownTitle(), isDatePickerModalOpen: dateTimePickerFromOpen || dateTimePickerToOpen, alignItemsRight: alignItemsRight, active: !disabled, disabled: disabled, roundedCorners: hideArrowButtons, externalControlSetOpen: setCustomDropdownOpen, externalControlOpen: customDropdownOpen }, { children: [!isMonthlyPicker &&
                        options &&
                        customDropdownOpen &&
                        options.map(({ title, fromDate, toDate }) => (_jsx(FilterDropdownTextItem, { title: title, onClick: () => {
                                setCustomPickerOpen(false);
                                setFilter({
                                    title,
                                    fromDate,
                                    toDate,
                                    isCustomRange: false,
                                    isMonthly: title === 'Last month' || title === 'This month',
                                });
                            }, disabled: fromDate && toDate && isDisableBefore({ fromDate, toDate }), closeOnClick: true }, title))), !disableCustomRange && customDropdownOpen && (_jsx(FilterDropdownTextItem, { title: "Custom date range", onClick: () => setCustomPickerOpen(!customPickerOpen) }, void 0)), customPickerOpen && !isMonthlyPicker && !isYearPicker && (_jsxs(_Fragment, { children: [_jsx(MuiPickersUtilsProvider, Object.assign({ utils: DateFnsUtils }, { children: _jsxs("div", Object.assign({ className: styles.customDateOpenContainerMargin }, { children: [customPickerVariant === 'datetime' &&
                                            !isMobile &&
                                            customDateTimePicker, customPickerVariant === 'datetime' &&
                                            isMobile &&
                                            customMobileDatePicker, customPickerVariant === 'date' &&
                                            !isMobile &&
                                            customDatePicker, customPickerVariant === 'date' &&
                                            isMobile &&
                                            customMobileDatePicker, !!customRangeErrorMessage && (_jsx("span", Object.assign({ className: styles.errorMessage }, { children: customRangeErrorMessage }), void 0))] }), void 0) }), void 0), _jsxs(FilterDropdownBasicItem, Object.assign({ className: styles.applyButton, closeOnClick: true }, { children: [_jsx(Button, Object.assign({ className: styles.cancel, size: "medium", variant: "solid", onClick: () => setCustomPickerOpen(!customPickerOpen) }, { children: "Cancel" }), void 0), _jsx(Button, Object.assign({ className: styles.button, size: "medium", variant: "solid", onClick: applyCustomDate, disabled: !!customRangeErrorMessage ||
                                            (customPickerVariant === 'date' && !customTo) }, { children: "Apply" }), void 0)] }), void 0)] }, void 0))] }), void 0)), !isMonthlyPicker && isYearPicker && (_jsx(FilterDropdown, Object.assign({ className: fullSize ? styles.fullSize : '', buttonClassName: classNames(styles.filterButton, fullSize ? styles.fullSize : ''), dropdownClassName: classNames(dropdownClassname, styles.noMaxHeight), title: selectedYear, alignItemsRight: alignItemsRight, active: !disabled, disabled: disabled, roundedCorners: hideArrowButtons, externalControlOpen: customPickerOpen, externalControlSetOpen: setCustomPickerOpen }, { children: customPickerOpen && isYearPicker && customYearPicker }), void 0)), isMonthlyPicker && (_jsx(FilterDropdown, Object.assign({ className: fullSize ? styles.fullSize : '', buttonClassName: classNames(styles.filterButton, fullSize ? styles.fullSize : ''), dropdownClassName: classNames(dropdownClassname, styles.noMaxHeight), title: renderFilterDropdownTitle(), alignItemsRight: alignItemsRight, active: !disabled, disabled: disabled, roundedCorners: hideArrowButtons, externalControlOpen: customPickerOpen, externalControlSetOpen: setCustomPickerOpen }, { children: customPickerOpen && isMonthlyPicker && customMonthlyPicker }), void 0)), !hideArrowButtons && (_jsx("div", Object.assign({ className: isRightArrowDisabled
                    ? styles.disabledRightArrowButtonContainer
                    : styles.rightArrowButtonContainer, onClick: isRightArrowDisabled ? undefined : () => onArrowButtonClick('right') }, { children: _jsx(IoMdArrowDropright, { size: 20, className: isRightArrowDisabled ? styles.disabledArrowIcon : styles.arrowIcon }, void 0) }), void 0))] }), void 0));
};
const useEffectResetFilterIfPreviousSelectionNoLongerAnOption = (setFilter, value, options, disableCustomRange) => {
    useEffect(() => {
        if (value === null || value === undefined || options.length === 0)
            return;
        if (value.isCustomRange === true &&
            disableCustomRange === true) {
            // If the old value was a custom range and custom range is now disabled it must be reset
            setFilter(options[0]);
        }
        else if (value.title === 'Custom date range') {
            // Do nothing. CustomDateRange is only used for reports
            return;
        }
        else if (disableCustomRange === false &&
            value.isCustomRange === false &&
            options.find(({ title }) => title === value.title) === undefined) {
            // If the old option title can't be found in the current options then the filter should be reset
            setFilter(options[0]);
        }
        // No deps, we want this to execute just once upon mount of a new filter as
        // that indicates accessing a new page that may have different options
        // available (primarily being that some pages don't use the Today filter)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
const useEffectUpdateDateFilterIfPlatformKeptOpen = (setOptions, optionCreator, setFilter, value) => {
    useEffect(() => {
        // Primary purpose of this is for filters like Today. If you open the
        // platform in the morning (say 11am) and don't close that webpage, when you
        // navigate that platform later in the day you don't want the Today filter
        // to still only show the data up to 11am.
        const newFilters = optionCreator();
        const updatedChosenFilter = newFilters.find(({ title: newFilterTitle }) => value && value.title === newFilterTitle);
        const didEitherDateChange = updatedChosenFilter !== undefined &&
            value &&
            value.toDate &&
            value.fromDate &&
            (!isSameMinute(updatedChosenFilter.toDate, value.toDate) ||
                !isSameMinute(updatedChosenFilter.fromDate, value.fromDate));
        const isZoomUpdate = value && value.isZoomingIn !== undefined && value.isZoomingIn;
        if (updatedChosenFilter &&
            didEitherDateChange &&
            value &&
            !isZoomUpdate &&
            value.title !== 'Custom date range') {
            setOptions(newFilters);
            setFilter(updatedChosenFilter);
        }
    }, [setOptions, optionCreator, setFilter, value]);
};
const getDateContainer = (dateString) => {
    const dates = dateString.split(' - ');
    const tokens = dateString.split(' ');
    if (dates.length !== 2 || dateString.indexOf(':') === -1) {
        // only has dates but not time info, also has no line break
        return _jsx("div", Object.assign({ className: styles.dateContainer }, { children: dateString }), void 0);
    }
    if (dates[1].indexOf('/') > -1) {
        // fromDate and toDate are not the same day
        return (_jsxs("div", Object.assign({ className: styles.dateTimeContainer }, { children: [_jsx("div", { children: `${dates[0]} -` }, void 0), _jsx("div", { children: dates[1] }, void 0)] }), void 0));
    }
    // same day
    return (_jsxs("div", Object.assign({ className: styles.dateTimeContainer }, { children: [_jsx("div", { children: tokens[0] }, void 0), _jsx("div", { children: tokens.slice(1).join(' ') }, void 0)] }), void 0));
};
export default withTheme(DateFilter);
